import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import once from 'lodash/once';

import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { TypeKeys, PhoneCallState } from '@common/react/store/PhoneCall';

import { ApplicationState } from '@app/store';

export interface PhoneCallResponse {
	token: string;
}

export interface PhoneCallProviderProps {
	children: any;
}

export interface PhoneCallProviderContextState {
	phoneCallState: PhoneCallState;
}

export interface PhoneCallProviderContextActions {
	clear: () => void;
	update: (date) => void;
}

export interface PhoneCallProviderContext {
	state: PhoneCallProviderContextState;
	actions: PhoneCallProviderContextActions;
}

export const createPhoneCallProviderContext = once(() => React.createContext({} as PhoneCallProviderContext));

export const usePhoneCallProviderContext: () => PhoneCallProviderContext = () =>
	React.useContext(createPhoneCallProviderContext());

const PhoneCallProvider: React.FC<PhoneCallProviderProps> = ({ children }) => {
	const Context = createPhoneCallProviderContext();
	const dispatch = useDispatch();
	const phoneCallState = useSelector((state: ApplicationState) => state.phoneCall);
	const { isActive } = phoneCallState;
	const { subscribe } = useApplicationContext();

	const clear = () => {
		dispatch({ type: TypeKeys.CLEAR });
	};
	const update = (data) => {
		dispatch({
			type: TypeKeys.UPDATE,
			data,
		});
	};

	const notificationHandle = (notification: Notification<BaseUser>) => {
		if (notification.objectType === 'PhoneCall') {
			if (notification.action === NotificationAction.Add) {
				dispatch({ type: TypeKeys.SETISBUSY, value: true });
			}

			if (notification.action === NotificationAction.Update && !isActive) {
				dispatch({ type: TypeKeys.SETISBUSY, value: false });
			}
		}
	};

	const value = {
		state: {
			phoneCallState,
		},
		actions: {
			clear,
			update,
		},
	};

	React.useEffect(subscribe(notificationHandle), []);

	return <Context.Provider value={value}>
		{children}
	</Context.Provider>;
};

export default PhoneCallProvider;
